import { Solutions, Solution, ContinueEntry, OAuthReturn } from './productEntry/surface';
import { Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DesktopShell } from './DesktopShell';
import { OnboardingGuard } from './redesign/components/OnboardingGuard';
import { TourProvider } from './redesign/components/Tour';
import { Home } from './redesign/pages/Home';
import { Assistant } from './redesign/pages/Assistant';
import { Activity } from './redesign/pages/Activity';
import { MyAutomations } from './redesign/pages/MyAutomations';
import { AutomationDetail } from './redesign/pages/AutomationDetail';
import { AutomationWizard } from './redesign/pages/AutomationWizard';
import { Agents } from './redesign/pages/Agents';
import { Connections } from './redesign/pages/Connections';
import { Knowledge } from './redesign/pages/Knowledge';
import { Byok } from './redesign/pages/Byok';
import { Legal } from './redesign/pages/Legal';
import { Changelog } from './redesign/pages/Changelog';
import { ChangelogDetail } from './redesign/pages/ChangelogDetail';
import { DeleteAccount } from './redesign/pages/DeleteAccount';
import { Feedback } from './redesign/pages/Feedback';
import { SupportProject } from './redesign/pages/SupportProject';
import { TasksReminders } from './redesign/pages/TasksReminders';
import { Marketplace } from './redesign/pages/Marketplace';
import { MarketplaceDetail } from './redesign/pages/MarketplaceDetail';
import { MarketplaceMine } from './redesign/pages/MarketplaceMine';
import { MarketplacePublish } from './redesign/pages/MarketplacePublish';
import { Guide } from './redesign/pages/Guide';
import Memory from '../pages/Memory';
import Projects from '../pages/Projects';
import './redesign/index.css';
import './redesign/reference.css';
import './desktop.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

export default function WebPlatformApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <TourProvider>
        <OnboardingGuard>
          <ContinueEntry />
          <Routes>
            <Route element={<DesktopShell />}>
              <Route index element={<Home />} />
              <Route path="assistant" element={<Assistant />} />
              <Route path="activity" element={<Activity />} />
              <Route path="automations" element={<MyAutomations />} />
              <Route path="automation/new" element={<AutomationDetail />} />
              <Route path="automation/wizard" element={<AutomationWizard />} />
              <Route path="automation/:id" element={<AutomationDetail />} />

              <Route path="settings" element={<SettingsRedirect />} />
              <Route path="settings/agents" element={<Agents />} />
              <Route path="settings/connections" element={<Connections />} />
              <Route path="settings/knowledge" element={<Knowledge />} />
              <Route path="settings/byok" element={<Byok />} />
              <Route path="settings/legal" element={<Legal />} />
              <Route path="settings/changelog" element={<Changelog />} />
              <Route path="settings/changelog/:version" element={<ChangelogDetail />} />
              <Route path="settings/support" element={<SupportProject />} />
              <Route path="settings/account" element={<DeleteAccount />} />
              <Route path="settings/feedback" element={<Feedback />} />
              <Route path="settings/tasks" element={<TasksReminders />} />
              <Route path="settings/reminders" element={<TasksReminders />} />

              <Route path="solutions" element={<Solutions />} />
              <Route path="solutions/link/:token" element={<Solution />} />
              <Route path="solutions/intent/:intentId" element={<Solution />} />
              <Route path="solutions/setup/:setupId" element={<Solution />} />
              <Route path="solutions/:id" element={<Solution />} />
              <Route path="marketplace" element={<Marketplace />} />
              <Route path="marketplace/:id" element={<MarketplaceDetail />} />
              <Route path="marketplace/mine" element={<MarketplaceMine />} />
              <Route path="marketplace/publish" element={<MarketplacePublish />} />

              <Route path="memory" element={<Memory />} />
              <Route path="projects" element={<Projects />} />
              <Route path="guide" element={<Guide />} />

              {/* Backward-compatible links from the previous desktop console. */}
              <Route path="chats" element={<Navigate to="/assistant" replace />} />
              <Route path="chats/:conversationId" element={<Navigate to="/assistant" replace />} />
              <Route path="agents" element={<Navigate to="/settings/agents" replace />} />
              <Route path="integrations" element={<OAuthReturn />} />
              <Route path="documents" element={<Navigate to="/settings/knowledge" replace />} />
              <Route path="pipelines" element={<Navigate to="/automations" replace />} />
              <Route path="tasks" element={<Navigate to="/settings/tasks" replace />} />
              <Route path="reminders" element={<Navigate to="/settings/reminders" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </OnboardingGuard>
      </TourProvider>
    </QueryClientProvider>
  );
}

// Kept as a tiny component so the Settings screen remains code-split-friendly
// if Vite chunks this route tree later.
import { Settings } from './redesign/pages/Settings';
function SettingsRedirect() { return <Settings />; }
